import { Typography } from '@/components/atoms/Typography';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useIsAdhaJourney, useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useAppStore } from '@/core/stores/appStore';
import { formatPhoneNumberDisplay } from '@/core/utils/phoneUtils';
import { ProfileContactCard } from '@/modules/profile/components/ProfileContactCard';
import { useRouter } from 'expo-router';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

function getPersonalDetailsPhoneDisplay(
    rawPhone: string,
    isNativeMobile: boolean,
): string {
    if (!rawPhone) {
        return '';
    }
    if (isNativeMobile) {
        return formatPhoneNumberDisplay(rawPhone);
    }
    return `+${rawPhone}`;
}

export default function PersonalDetailsScreen() {
    const isAdhaJourney = useIsAdhaJourney();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const colors = useUIColors();
    const router = useRouter();
    const user = useAppStore((state) => state.auth.user);
    const rawPhone = user?.primary_phone || '';
    const isNativeMobile = Platform.OS !== 'web';
    const displayPhone = getPersonalDetailsPhoneDisplay(rawPhone, isNativeMobile);

    useCatalogDynatraceView(DT_SCREEN.PERSONAL_DETAILS, 'personal_details_viewed', true);
    useCatalogDynatraceView(
        ALMETLAA_SCREEN.PROFILE,
        ALMETLAA_ACTION.PROFILE.PERSONAL_DETAILS_VIEW,
        isAdhaJourney,
    );

    const handlePhoneEdit = () => {
        const currentPhone = user?.primary_phone || '';
        const countryCode = user?.primary_country_code || '+971';
        // Construct full phone number: if phone already has country code, use as-is, otherwise prepend country code
        let fullPhone = currentPhone;
        if (currentPhone && !currentPhone.startsWith('+')) {
            // Remove + from country code if present, then combine
            const codeWithoutPlus = countryCode.replace('+', '');
            fullPhone = `${codeWithoutPlus}${currentPhone}`;
        } else if (!currentPhone) {
            // If no phone, just use country code
            fullPhone = countryCode.replace('+', '');
        }
        router.push({
            pathname: '/(auth)/profile-phone-otp-inline',
            params: {
                contactInfo: fullPhone,
                progress: '0.7',
            },
        } as any);
    };

    const handleLoginEmailEdit = () => {
        const currentEmail = user?.primary_email || '';
        router.push({
            pathname: '/(auth)/profile-login-email-otp-inline',
            params: {
                contactInfo: currentEmail,
                progress: '0.8',
            },
        } as any);
    };

    const handleEmailEdit = () => {
        const currentEmail = user?.secondary_email || user?.primary_email || '';
        router.push({
            pathname: '/(auth)/profile-email-otp-inline',
            params: {
                contactInfo: currentEmail,
                progress: '0.8',
            },
        } as any);
    };

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader hideLogo={true} showLanguageSwitch={false} />

            <View style={styles.content}>
                <Typography variant="title1" color="primary" style={{ marginBottom: 16 }}>
                    {t('profile.personalDetails')}
                </Typography>

                {/* Login email id */}
                <ProfileContactCard
                    title={t('profile.loginEmailId' as any)}
                    value={
                        <Typography variant="body" color="secondary">
                            {user?.primary_email || ''}
                        </Typography>
                    }
                    {...(isAdhaJourney
                        ? { helperText: t('profile.loginEmailCannotBeChanged' as any) }
                        : { onEdit: handleLoginEmailEdit })}
                />

                {/* Phone number */}
                <ProfileContactCard
                    title={t('profile.phoneNumber')}
                    value={
                        <View style={{ direction: 'ltr' }}>
                            <Typography
                                variant="body"
                                color="secondary"
                                style={{
                                    writingDirection: 'ltr',
                                    textAlign: isRTL ? 'right' : 'left',
                                }}
                            >
                                {displayPhone}
                            </Typography>
                        </View>
                    }
                    {...(!isAdhaJourney ? { onEdit: handlePhoneEdit } : {})}
                />

                {/* Communication email id */}
                {!isAdhaJourney && (
                    <ProfileContactCard
                        title={t('profile.communicationEmailId' as any)}
                        value={
                            <Typography variant="body" color="secondary">
                                {user?.secondary_email || user?.primary_email || ''}
                            </Typography>
                        }
                        helperText={t('profile.communicationEmailDescription' as any)}
                        onEdit={handleEmailEdit}
                    />
                )}

            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    content: {
        paddingTop: 32,
        paddingHorizontal: 16,
        gap: 12,
    },
});
